import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CaseTableItem } from 'src/app/shared/payee-cases/types';
import { ClientDataTableItem } from 'src/app/services/clients/clients.service';
import { CasesService } from 'src/app/services/cases/cases.service';
import {
  CountryProduct,
  CountryProductService,
} from 'src/app/services/country-product/country-product.service';
import {
  Product,
  ProductService,
} from 'src/app/services/product-service/product-service.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-create-case-dialog',
  templateUrl: './create-case-dialog.component.html',
  styleUrls: ['./create-case-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
  ],
})
export class CreateCaseDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<CreateCaseDialogComponent, CaseTableItem | undefined>>(
      MatDialogRef,
    );
  private fb = inject(FormBuilder);
  private snackBar = inject(MatSnackBar);
  private casesService = inject(CasesService);
  private countryProductService = inject(CountryProductService);
  private productService = inject(ProductService);

  data = inject<ClientDataTableItem>(MAT_DIALOG_DATA);

  readonly jurisdictionControl: FormControl<CountryProduct | null>;
  readonly productControl: FormControl<Product | null>;

  jurisdictionOptions: CountryProduct[] = [];
  productOptions: Product[] = [];
  loading = false;

  constructor() {
    this.jurisdictionControl = this.fb.control<CountryProduct | null>(null, [
      Validators.required,
    ]);
    this.productControl = this.fb.control<Product | null>(null, [
      Validators.required,
    ]);
  }

  async ngOnInit(): Promise<void> {
    this.jurisdictionOptions = await this.countryProductService.getCountries();
    this.jurisdictionControl.valueChanges.subscribe(
      async (value: CountryProduct | string | null) => {
        this.productControl.setValue(null);
        if (!value || typeof value !== 'object' || !value.iso) {
          this.productOptions = [];
          return;
        }
        const result = await this.productService.getProducts({
          debtor_country_iso: value.iso,
        });
        this.productOptions = result.products.filter(
          (p) => p.type !== 'hard_2',
        );
      },
    );
  }

  jurisdictionDisplayWith(c?: CountryProduct | null): string {
    return c?.name ?? '';
  }

  productDisplayWith(p?: Product | null): string {
    return p?.name ?? '';
  }

  async submit(): Promise<void> {
    if (this.jurisdictionControl.invalid || this.productControl.invalid) {
      this.jurisdictionControl.markAsTouched();
      this.productControl.markAsTouched();
      return;
    }

    const jurisdiction = this.jurisdictionControl.value as CountryProduct;
    const product = this.productControl.value as Product;
    if (!jurisdiction?.iso || !product?.type) {
      return;
    }

    try {
      this.loading = true;
      const caseItem = await this.casesService.createCase({
        client_id: this.data.id,
        jurisdiction: jurisdiction.iso,
        product_type: product.type,
      });
      this.dialogRef.close(caseItem);
    } catch (error) {
      console.error('Error while creating case', error);
      this.snackBar.open('Ügy létrehozása sikertelen', 'OK', {
        duration: 5000,
      });
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
